import { Column, Entity } from 'typeorm';
import { BaseEntity } from './base.entity';

/**
 * Scoring weights for opportunity prioritization.
 */
export interface ReconScoringWeights {
	intentStrength: number;
	icpFit: number;
	authorAuthority: number;
	recency: number;
	engagement: number;
	reachability: number;
	competitorRisk: number;
}

/**
 * Default scoring weights for new settings.
 * MySQL does not allow DEFAULT values on JSON columns, so defaults are applied in application code.
 */
export const DEFAULT_RECON_SCORING_WEIGHTS: ReconScoringWeights = {
	intentStrength: 0.3,
	icpFit: 0.25,
	authorAuthority: 0.15,
	recency: 0.1,
	engagement: 0.05,
	reachability: 0.1,
	competitorRisk: 0.05,
};

/**
 * Default business hours days (Mon-Fri).
 * MySQL does not allow DEFAULT values on JSON columns, so defaults are applied in application code.
 */
export const DEFAULT_RECON_BUSINESS_HOURS_DAYS: Array<number> = [1, 2, 3, 4, 5];

/**
 * Recon settings per tenant (organization).
 * Stores company profile, ICP definition, scoring weights, and behavior settings.
 */
@Entity('recon_settings')
export class ReconSettingsEntity extends BaseEntity {
	@Column('tinyint', { name: 'enabled', default: 0, width: 1 })
	public enabled!: boolean;

	/**
	 * Company profile for AI context.
	 * {
	 *   companyName, companyDescription, industry, targetIndustries,
	 *   brandVoice: { tone, doList, dontList },
	 *   contentInterests, preferredPostTypes
	 * }
	 */
	@Column('json', { name: 'company_profile', nullable: true })
	public companyProfile!: Record<string, unknown> | null;

	/**
	 * Ideal Customer Profile definition.
	 * { industries, companySizeRange: { min, max }, titles, geographies }
	 */
	@Column('json', { name: 'icp_definition', nullable: true })
	public icpDefinition!: Record<string, unknown> | null;

	/**
	 * Scoring weights for opportunity prioritization.
	 * Use DEFAULT_RECON_SCORING_WEIGHTS when creating new records.
	 */
	@Column('json', { name: 'scoring_weights', nullable: true })
	public scoringWeights!: ReconScoringWeights | null;

	// Business hours (for interactions only, not data pulls)
	@Column('varchar', {
		name: 'business_hours_timezone',
		length: 50,
		default: 'America/Detroit',
	})
	public businessHoursTimezone!: string;

	@Column('time', { name: 'business_hours_start', default: "'08:00:00'" })
	public businessHoursStart!: string;

	@Column('time', { name: 'business_hours_end', default: "'18:00:00'" })
	public businessHoursEnd!: string;

	/**
	 * Business hours days (1=Mon, 7=Sun).
	 * Use DEFAULT_RECON_BUSINESS_HOURS_DAYS when creating new records.
	 */
	@Column('json', { name: 'business_hours_days', nullable: true })
	public businessHoursDays!: Array<number> | null;

	// Rate limiting (human behavior simulation)
	@Column('int', { name: 'min_delay_between_actions_ms', default: 30000 })
	public minDelayBetweenActionsMs!: number;

	@Column('int', { name: 'max_delay_between_actions_ms', default: 180000 })
	public maxDelayBetweenActionsMs!: number;

	@Column('int', { name: 'max_actions_per_hour', default: 10 })
	public maxActionsPerHour!: number;

	/**
	 * AI configuration (OpenAI API key, etc.)
	 * { openaiApiKey, model, temperature }
	 */
	@Column('json', { name: 'ai_config', nullable: true })
	public aiConfig!: Record<string, unknown> | null;

	// Notifications
	@Column('tinyint', { name: 'notify_on_high_priority', default: 1, width: 1 })
	public notifyOnHighPriority!: boolean;

	@Column('tinyint', { name: 'daily_digest_enabled', default: 1, width: 1 })
	public dailyDigestEnabled!: boolean;

	@Column('time', { name: 'daily_digest_time', default: "'08:00:00'" })
	public dailyDigestTime!: string;

	/**
	 * Keywords to monitor across platforms.
	 * Can be industry terms, competitor names, product categories, etc.
	 */
	@Column('json', { name: 'keywords', nullable: true })
	public keywords!: Array<string> | null;

	/**
	 * Platform-specific settings.
	 * {
	 *   discord: { monitoredGuildIds, monitoredChannelIds },
	 *   twitter: { monitoredHashtags, monitoredAccounts },
	 *   ...
	 * }
	 */
	@Column('json', { name: 'platform_settings', nullable: true })
	public platformSettings!: Record<string, unknown> | null;
}
